import { CustomWidgetCollection, Question, Serializer } from "survey-core";
import { ComponentDataSourceService } from "../services/component-data-source/component-data-source.service";
import { firstValueFrom, of } from "rxjs";
import { DualPanelItem } from "../interfaces/DualPanelItem.interface";
import { LoadDataParams } from '../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../environment/environment';

interface DualPanelRuntime {
  availableItems: DualPanelItem[];
  selectedItems: DualPanelItem[];
  currentSearchTerm: string;
  /** Checked rows on the left panel, kept across search re-renders */
  pendingAvailableSelection: Set<string>;
  rendering?: boolean;
  handlersReady?: boolean;
  clickBound?: boolean;
}

function getDualPanelRuntime(question: { __dualPanelRuntime?: DualPanelRuntime }): DualPanelRuntime {
  if (!question.__dualPanelRuntime) {
    question.__dualPanelRuntime = {
      availableItems: [],
      selectedItems: [],
      currentSearchTerm: '',
      pendingAvailableSelection: new Set(),
    };
  }
  return question.__dualPanelRuntime;
}

// Create a custom question type
class DualPanelQuestion extends Question {
  override getType() {
    return "dualpanel";
  }

  get choices() {
    return this.getPropertyValue("choices");
  }
  set choices(newValue) {
    this.setPropertyValue("choices", newValue);
  }

  get dataSource() {
    return this.getPropertyValue("dataSource");
  }
  set dataSource(newValue) {
    this.setPropertyValue("dataSource", newValue);
  }

  get selectAllOnSearch() {
    return this.getPropertyValue("selectAllOnSearch");
  }
  set selectAllOnSearch(newValue) {
    this.setPropertyValue("selectAllOnSearch", newValue);
  }

  get searchPlaceholder() {
    return this.getPropertyValue("searchPlaceholder");
  }
  set searchPlaceholder(newValue) {
    this.setPropertyValue("searchPlaceholder", newValue);
  }
}

// Register the question type
Serializer.addClass(
  "dualpanel",
  [
    { 
      name: "choices:itemvalues", 
      default: [
        { name: "JavaScript", type: "Frontend" },
        { name: "Python", type: "Backend" },
        { name: "Java", type: "Backend" },
        { name: "TypeScript", type: "Frontend" }
      ] 
    },
    {
      name: "dataSource",
      type: "string",
      default: "",
      category: "general"
    },
    {
      name: "selectAllOnSearch:boolean",
      default: false,
      category: "general"
    },
    {
      name: "searchPlaceholder",
      type: "string",
      default: "Søg...",
      category: "general"
    }
  ],
  function() {
    return new DualPanelQuestion("dualpanel");
  },
  "question"
);

// Define the custom widget
export const dualPanelSelector = {
  name: "dualpanel",
  title: "Dual Panel Selector",
  iconName: "icon-panel",
  category: "Custom Questions",
  widgetIsLoaded: () => true,
  isFit: (question: any) => question.getType() === "dualpanel",
  activatedByChanged: (activatedBy: string) => activatedBy === "property",
  isDefaultRender: false,
  htmlTemplate: "<div class='dual-panel-container'></div>",
  afterRender: async (question: any, element: any) => {
    if (!question || !element) return;

    // Get the component data source service from the window object
    // This is a workaround since we can't inject services into SurveyJS widgets
    const dataSourceService = (window as any).componentDataSourceService as ComponentDataSourceService;
    
    if (!dataSourceService) {
      console.error('[DualPanel] No component data source service available in window object');
      return;
    }
    

    // Check for existing containers and remove any additional ones to prevent duplicates
    const existingContainers = element.querySelectorAll('.dual-panel-container');
    if (existingContainers.length > 1) {
      for (let i = 1; i < existingContainers.length; i++) {
        existingContainers[i].remove();
      }
    }

    // Create container (or use existing one)
    const container = element.querySelector('.dual-panel-container');
    if (!container) return;

    const runtime = getDualPanelRuntime(question as { __dualPanelRuntime?: DualPanelRuntime });
    if (!runtime.pendingAvailableSelection) {
      runtime.pendingAvailableSelection = new Set();
    }

    if (runtime.rendering) {
      return;
    }

    if ((question as { __dualPanelInitialized?: boolean }).__dualPanelInitialized) {
      const hasUi = container.querySelector('.button-panel');
      const itemCount =
        (runtime.availableItems?.length ?? 0) + (runtime.selectedItems?.length ?? 0);
      const shouldSkip =
        hasUi &&
        runtime.handlersReady &&
        (itemCount > 0 || question.isVisible === false);
      if (shouldSkip) {
        return;
      }
      if (hasUi) {
        delete (question as { __dualPanelInitialized?: boolean }).__dualPanelInitialized;
        runtime.handlersReady = false;
        runtime.rendering = false;
        container.innerHTML = '';
      }
    }

    runtime.rendering = true;

    try {
    // Clear any existing content to prevent duplicates
    const existingDescriptionModal = document.querySelector('.dual-panel-description-modal-overlay') as (HTMLElement & { __keydownHandler?: (event: KeyboardEvent) => void }) | null;
    if (existingDescriptionModal?.__keydownHandler) {
      document.removeEventListener('keydown', existingDescriptionModal.__keydownHandler);
    }
    existingDescriptionModal?.remove();
    container.innerHTML = '';

    const escapeHtml = (text: string): string => {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    const DESCRIPTION_LONG_THRESHOLD = 120;
    let descriptionModal: HTMLElement | null = null;
    let descriptionModalKeyHandler: ((event: KeyboardEvent) => void) | null = null;

    const isLongDescription = (description: string): boolean => {
      const trimmed = description.trim();
      return trimmed.length > DESCRIPTION_LONG_THRESHOLD || (trimmed.match(/\n/g)?.length ?? 0) >= 2;
    };

    const closeDescriptionModal = () => {
      if (descriptionModalKeyHandler) {
        document.removeEventListener('keydown', descriptionModalKeyHandler);
        descriptionModalKeyHandler = null;
      }
      descriptionModal?.remove();
      descriptionModal = null;
    };

    const openDescriptionModal = (title: string, description: string) => {
      closeDescriptionModal();

      const overlay = document.createElement('div');
      overlay.className = 'dual-panel-description-modal-overlay';

      const modal = document.createElement('div');
      modal.className = 'dual-panel-description-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');

      const header = document.createElement('div');
      header.className = 'dual-panel-description-modal-header';

      const titleEl = document.createElement('h3');
      titleEl.className = 'dual-panel-description-modal-title';
      titleEl.textContent = title;

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'dual-panel-description-modal-close';
      closeBtn.setAttribute('aria-label', 'Luk');
      closeBtn.textContent = '×';

      const body = document.createElement('div');
      body.className = 'dual-panel-description-modal-body';
      body.textContent = description;

      closeBtn.addEventListener('click', closeDescriptionModal);
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          closeDescriptionModal();
        }
      });

      header.appendChild(titleEl);
      header.appendChild(closeBtn);
      modal.appendChild(header);
      modal.appendChild(body);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
      descriptionModal = overlay;

      descriptionModalKeyHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeDescriptionModal();
        }
      };
      document.addEventListener('keydown', descriptionModalKeyHandler);
      (overlay as HTMLElement & { __keydownHandler?: (event: KeyboardEvent) => void }).__keydownHandler = descriptionModalKeyHandler;
      closeBtn.focus();
    };

    const attachDescriptionReadMoreHandlers = (listEl: HTMLElement, items: DualPanelItem[]) => {
      listEl.querySelectorAll('.description-read-more').forEach(button => {
        const index = Number(button.getAttribute('data-desc-index'));
        const item = items[index];
        if (!item?.description?.trim()) {
          return;
        }

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openDescriptionModal(item.name, item.description!.trim());
        });
      });
    };

    const selectAllOnSearchEnabled = question.selectAllOnSearch === true;

    // Create left panel
    const leftPanel = document.createElement("div");
    leftPanel.className = "left-panel";
    container.appendChild(leftPanel);

    // Add search box
    const searchContainer = document.createElement("div");
    searchContainer.className = "search-container";
    leftPanel.appendChild(searchContainer);

    const searchBox = document.createElement("input");
    searchBox.type = "text";
    searchBox.className = "search-input";
    searchBox.placeholder = question.searchPlaceholder || "Søg...";
    searchContainer.appendChild(searchBox);

    const selectAllContainer = document.createElement("div");
    selectAllContainer.className = "select-all-container";
    selectAllContainer.style.display = "none";
    searchContainer.appendChild(selectAllContainer);

    const selectAllCheckbox = document.createElement("input");
    selectAllCheckbox.type = "checkbox";
    selectAllCheckbox.id = `select_all_${question.name || 'items'}`;
    selectAllCheckbox.className = "select-all-checkbox";
    selectAllContainer.appendChild(selectAllCheckbox);

    const selectAllLabel = document.createElement("label");
    selectAllLabel.htmlFor = selectAllCheckbox.id;
    selectAllLabel.className = "select-all-label";
    selectAllLabel.textContent = "Vælg alle søgeresultater";
    selectAllContainer.appendChild(selectAllLabel);

    // Add available items list
    const availableList = document.createElement("div");
    availableList.className = "items-list available-items";
    leftPanel.appendChild(availableList);

    // Create middle panel with buttons
    const buttonPanel = document.createElement("div");
    buttonPanel.className = "button-panel";
    container.appendChild(buttonPanel);

    const moveRightButton = document.createElement("button");
    moveRightButton.type = "button";
    moveRightButton.className = "arrow-button move-right";
    moveRightButton.textContent = "→";
    buttonPanel.appendChild(moveRightButton);

    const moveLeftButton = document.createElement("button");
    moveLeftButton.type = "button";
    moveLeftButton.className = "arrow-button move-left";
    moveLeftButton.textContent = "←";
    buttonPanel.appendChild(moveLeftButton);

    // Create right panel
    const rightPanel = document.createElement("div");
    rightPanel.className = "right-panel";
    container.appendChild(rightPanel);

    const selectedList = document.createElement("div");
    selectedList.className = "items-list selected-items";
    rightPanel.appendChild(selectedList);

    (question as { __dualPanelInitialized?: boolean }).__dualPanelInitialized = true;

    // Process items to ensure consistent format
    const processItems = (items: any[]): DualPanelItem[] => {
      const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };

      /** Stable row id when survey JSON sets dataSource*Field to paths that don't exist on this data source (e.g. form-level guid + dual_panel_users rows with uuid). */
      const idFallback = (row: any): any => {
        if (!row || typeof row !== 'object') return row;
        return row.item_id !== undefined
          ? row.item_id
          : row.id !== undefined
            ? row.id
            : row.uuid !== undefined
              ? row.uuid
              : row.guid !== undefined
                ? row.guid
                : row.user_id !== undefined
                  ? row.user_id
                  : row.users_id !== undefined
                    ? row.users_id
                    : row.user_uuid !== undefined
                      ? row.user_uuid
                      : row.value !== undefined
                        ? row.value
                        : row;
      };

      return items.map(item => {
        if (typeof item === 'string') {
          return { value: item, name: item };
        } else if (typeof item === 'number') {
          // Handle number values directly
          return { value: item, name: String(item) };
        } else if (typeof item === 'object') {
          let value: any;
          if (question.dataSourceValueField) {
            value = getNestedValue(item, question.dataSourceValueField);
          } else if (question.dataSourceKeyField) {
            value = getNestedValue(item, question.dataSourceKeyField);
          }
          if (value === undefined || value === null || value === '') {
            value = idFallback(item);
          }
          if (value !== null && typeof value === 'object') {
            const inner = idFallback(value);
            if (inner !== value && inner !== undefined && inner !== null && inner !== '') {
              value = inner;
            }
          }

          let name: any;
          if (question.dataSourceKeyField) {
            name = getNestedValue(item, question.dataSourceKeyField);
          }
          if (name === undefined || name === null || name === '') {
            name = item.name || item.text || item.label || item.title || String(value ?? '') || '';
          }

          return { 
            value: value,
            name: name,
            type: item.type || '',
            description: item.description || '',
            searchText: item.searchText || '',
            disabled: item.disabled === true,
            email: item.email || ''
          };
        }
        return { value: item, name: String(item) };
      });
    };

    // Load items from data source if specified
    let items: any[] = [];
    let selectedItems: DualPanelItem[] = runtime.selectedItems;
    const questionName = question.name || 'items';
    
    if (question.dataSource && dataSourceService) {
      try {
        
        // Extract pre-selected values from question.value
        const currentValue = question.value;
        
        let selectedValues: any[] = [];
        
        // Extract pre-selected values from question.value with improved logic
        if (currentValue !== undefined && currentValue !== null) {
          
          if (Array.isArray(currentValue)) {
            // Direct array of values
            selectedValues = currentValue;
          } else if (typeof currentValue === 'object') {
            
            // First check for special event form structure
            if (questionName === 'event_subtypes' && currentValue.event_subtypes) {
              selectedValues = Array.isArray(currentValue.event_subtypes) ? currentValue.event_subtypes : [currentValue.event_subtypes];
            } 
            else if (questionName === 'event_modules' && currentValue.event_modules) {
              selectedValues = Array.isArray(currentValue.event_modules) ? currentValue.event_modules : [currentValue.event_modules];
            }
            else if (questionName === 'event_users' && currentValue.event_users) {
              selectedValues = Array.isArray(currentValue.event_users) ? currentValue.event_users : [currentValue.event_users];
            }
            else if (questionName === 'add_event_users' && currentValue.add_event_users) {
              selectedValues = Array.isArray(currentValue.add_event_users)
                ? currentValue.add_event_users
                : [currentValue.add_event_users];
            }
            // Then check standard property with question name
            else if (currentValue[questionName] && Array.isArray(currentValue[questionName])) {
              selectedValues = currentValue[questionName];
            } 
            // Check if the object itself contains item_id properties (for API data)
            else if (currentValue.item_id !== undefined) {
              selectedValues = [currentValue.item_id];
            }
            // Check if it's an array of objects with item_id
            else if (Array.isArray(currentValue) && currentValue.length > 0 && currentValue[0].item_id !== undefined) {
              selectedValues = currentValue.map(item => item.item_id);
            }
            else {
              // Try to extract values from any property that's an array
              for (const key in currentValue) {
                if (Array.isArray(currentValue[key])) {
                  selectedValues = currentValue[key];
                  break;
                }
              }
              
              // If we couldn't find an array property, try to use object values
              if (selectedValues.length === 0) {
                const objectValues = Object.values(currentValue);
                
                // Check if any of the values are arrays
                for (const val of objectValues) {
                  if (Array.isArray(val)) {
                    selectedValues = val;
                    break;
                  }
                }
                
                // If still no array found, flatten all values
                if (selectedValues.length === 0) {
                  selectedValues = objectValues.flat().filter(v => v !== null && v !== undefined);
                }
              }
            }
          } else {
            // If a single value, convert to array
            selectedValues = [currentValue];
          }
        }
        
        // Filter out any null/undefined values and ensure we have valid IDs
        selectedValues = selectedValues.filter(v => v !== null && v !== undefined && v !== '');
        
        // First, if we have selected values, make a request to fetch them by ID
        if (selectedValues.length > 0) {
          
          try {
            const dataSourceName = question.dataSource;
            
            // Create a special request specifically for the selected IDs
            const selectedItemsParams: LoadDataParams = {
              dataSourceKey: dataSourceName,
              filters: [
                { column: 'ids', value: selectedValues, operator: 'in' }
              ],
              queryParams: {
                ids: selectedValues.join(','),
                includeSelectedIds: true,
                useByIdsEndpoint: true,
                pageSize: 1000,
                _byIds: "true"
              }
            };
            
            // Special handling for event subtypes and modules to ensure we convert item_id to value
            if (questionName === 'event_subtypes' || questionName === 'event_modules' || questionName === 'event_users') {
              
              // Create placeholder items with the values so they'll be visible even if fetch fails
              selectedItems = selectedValues.map(val => ({
                value: val,
                name: `Loading item ${val}...`,
                type: questionName === 'event_subtypes' ? 'subtype' : (questionName === 'event_modules' ? 'module' : 'user')
              }));
            }
            
            
            // Try to get the data source and execute it
            const dataSource = dataSourceService.getDataSource(dataSourceName);
            if (!dataSource) {
              console.error(`[DualPanel] Data source '${dataSourceName}' not found`);
              return;
            }
            
            const selectedItemsResult = await firstValueFrom(dataSource.execute(selectedItemsParams) || of(null)).catch(error => {
              console.error(`[DualPanel] Error in by-ids request:`, error);
              return null;
            });
            
            if (selectedItemsResult) {
              
              // Process the selected items
              let fetchedSelectedItems: any[] = [];
              if (Array.isArray(selectedItemsResult)) {
                fetchedSelectedItems = processItems(selectedItemsResult);
              } else if (typeof selectedItemsResult === 'object') {
                if ('data' in selectedItemsResult && Array.isArray(selectedItemsResult.data)) {
                  fetchedSelectedItems = processItems(selectedItemsResult.data);
                } else if ('items' in selectedItemsResult && Array.isArray(selectedItemsResult.items)) {
                  fetchedSelectedItems = processItems(selectedItemsResult.items);
                }
              }
              
              // If we got actual data, replace the placeholder items
              if (fetchedSelectedItems.length > 0) {
                selectedItems = fetchedSelectedItems;
              } else {
              }
              
            } else {
            }
          } catch (error) {
            console.error(`[DualPanel] Error fetching selected items:`, error);
            // Keep placeholder items if fetch fails
          }
        }
        
        // Now fetch the regular items for the available panel
        
        // Get the data source name
        const dataSourceName = question.dataSource;
        
        const initialItemsParams: LoadDataParams = {
          dataSourceKey: dataSourceName,
          filters: [
            { column: 'pageSize', value: 150, operator: 'equals' },
            { column: 'pageIndex', value: 0, operator: 'equals' }
          ],
          queryParams: {
            pageSize: 150,
            pageIndex: 0
          }
        };
        
        
        // Try to get the data source and execute it
        const dataSource = dataSourceService.getDataSource(dataSourceName);
        if (!dataSource) {
          console.error(`[DualPanel] Data source '${dataSourceName}' not found for regular request`);
          return;
        }
        
        const result = await firstValueFrom(dataSource.execute(initialItemsParams) || of(null)).catch(error => {
          console.error(`[DualPanel] Error in regular request:`, error);
          return null;
        });
        
        if (result) {
          
          // Process the items
          if (Array.isArray(result)) {
            items = processItems(result);
          } else if (typeof result === 'object') {
            if ('data' in result && Array.isArray(result.data)) {
              items = processItems(result.data);
            } else if ('items' in result && Array.isArray(result.items)) {
              items = processItems(result.items);
            }
          }
          
          // Extract assigned IDs from disabled items and store on question for extra guard
          try {
            const assignedValues = items
              .filter((it: any) => it && it.disabled === true)
              .map((it: any) => it.value)
              .filter((v: any) => v !== undefined && v !== null && v !== '');
            (question as any)['assignedUserIds'] = new Set(assignedValues.map(String));
          } catch (e) {
            console.warn('[DualPanel] Failed to capture assigned user IDs', e);
          }

        }
        
      } catch (error) {
        console.error('[DualPanel] Error loading data source:', error);
        items = processItems(question.choices || []);
      }
    } else {
      // No data source, use static choices
      items = processItems(question.choices || []);
      
      // Still need to extract selected values for static choices
      const currentValue = question.value;
      let selectedValues: any[] = [];
      
      if (currentValue !== undefined && currentValue !== null) {
        
        if (Array.isArray(currentValue)) {
          selectedValues = currentValue;
        } else if (typeof currentValue === 'object') {
          // Check for special event form structure
          if (questionName === 'event_subtypes' && currentValue.event_subtypes) {
            selectedValues = Array.isArray(currentValue.event_subtypes) ? currentValue.event_subtypes : [currentValue.event_subtypes];
          } 
          else if (questionName === 'event_modules' && currentValue.event_modules) {
            selectedValues = Array.isArray(currentValue.event_modules) ? currentValue.event_modules : [currentValue.event_modules];
          }
          else if (questionName === 'event_users' && currentValue.event_users) {
            selectedValues = Array.isArray(currentValue.event_users) ? currentValue.event_users : [currentValue.event_users];
          }
          else if (questionName === 'add_event_users' && currentValue.add_event_users) {
            selectedValues = Array.isArray(currentValue.add_event_users)
              ? currentValue.add_event_users
              : [currentValue.add_event_users];
          }
          else if (currentValue[questionName] && Array.isArray(currentValue[questionName])) {
            selectedValues = currentValue[questionName];
          } else {
            selectedValues = Object.values(currentValue).flat().filter(v => v !== null && v !== undefined);
          }
        } else {
          selectedValues = [currentValue];
        }
        
        // Filter out any null/undefined values
        selectedValues = selectedValues.filter(v => v !== null && v !== undefined && v !== '');
        
        // Split items between selected and available based on the values
        if (selectedValues.length > 0) {
          const selectedValueSet = new Set(selectedValues.map(v => String(v)));
          selectedItems = items.filter(item => selectedValueSet.has(String(item.value)));
        }
      }
    }
    
    
    // Prepare available items (not in selected items)
    let availableItems: DualPanelItem[] = runtime.availableItems;
    
    
    // Always split items between available and selected based on what we have
    if (selectedItems.length > 0) {
      
      // Create a set of selected item values for efficient lookup
      const selectedItemValues = new Set(selectedItems.map((item: DualPanelItem) => String(item.value)));
      
      // Filter available items to exclude those that are already selected
      availableItems = items.filter((item: DualPanelItem) => !selectedItemValues.has(String(item.value)));
      
    } else {
      availableItems = [...items];
    }
    

    runtime.availableItems = availableItems;
    runtime.selectedItems = selectedItems;

    // Helper to check if any items have types
    const hasTypes = (items: DualPanelItem[]): boolean => {
      return items.some(item => item.type !== undefined && item.type !== '');
    };

    const hasDescriptions = (items: DualPanelItem[]): boolean => {
      return items.some(item => !!item.description?.trim());
    };

    const itemMatchesSearch = (item: DualPanelItem, searchTerm: string): boolean => {
      if (!searchTerm) {
        return true;
      }
      return item.name.toLowerCase().includes(searchTerm) ||
        (item.type?.toLowerCase().includes(searchTerm) ?? false) ||
        (item.description?.toLowerCase().includes(searchTerm) ?? false) ||
        (item.searchText?.toLowerCase().includes(searchTerm) ?? false);
    };

    const isAvailableItemPending = (value: string): boolean =>
      runtime.pendingAvailableSelection.has(String(value));

    const setAvailableItemPending = (value: string, pending: boolean) => {
      const key = String(value);
      if (pending) {
        runtime.pendingAvailableSelection.add(key);
      } else {
        runtime.pendingAvailableSelection.delete(key);
      }
    };

    const updateSelectAllState = () => {
      if (!selectAllOnSearchEnabled || !runtime.currentSearchTerm) {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
        return;
      }

      const visibleCheckboxes = Array.from(
        availableList.querySelectorAll("input[type='checkbox']:not(:disabled)") as NodeListOf<HTMLInputElement>
      );
      const checkedCount = visibleCheckboxes.filter(cb => isAvailableItemPending(cb.value)).length;

      selectAllCheckbox.checked = visibleCheckboxes.length > 0 && checkedCount === visibleCheckboxes.length;
      selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < visibleCheckboxes.length;
    };

    const updateSelectAllVisibility = () => {
      if (!selectAllOnSearchEnabled) {
        selectAllContainer.style.display = "none";
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
        return;
      }

      const shouldShow = runtime.currentSearchTerm.length > 0;
      selectAllContainer.style.display = shouldShow ? "flex" : "none";
      if (!shouldShow) {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
      }
    };

    const renderItemRow = (
      item: DualPanelItem,
      index: number,
      inputId: string,
      inputName: string,
      showTypes: boolean,
      showDescriptions: boolean,
      disabled = false
    ): string => {
      const description = item.description?.trim() ?? '';
      const descriptionBlock = showDescriptions && description ? `
        <div class="item-description-wrap">
          <span class="item-description">${escapeHtml(description)}</span>
          ${isLongDescription(description) ? `<button type="button" class="description-read-more" data-desc-index="${index}">Læs mere</button>` : ''}
        </div>
      ` : '';
      const tooltip = [item.email, item.searchText, !descriptionBlock ? description : '']
        .map(value => value?.trim())
        .filter((value): value is string => !!value)
        .join('\n');

      return `
        <div class="item${showTypes ? ' two-column' : ''}${descriptionBlock ? ' has-description' : ''}${disabled ? ' disabled' : ''}">
          <input type="checkbox" name="${inputName}" value="${item.value}" id="${inputId}" ${disabled ? 'disabled' : ''}>
          <div class="item-body">
            <label for="${inputId}" title="${tooltip ? escapeHtml(tooltip) : ''}">
              <span class="item-name">${escapeHtml(item.name)}</span>
              ${showTypes && item.type ? `<span class="item-type">${escapeHtml(item.type)}</span>` : ''}
              ${disabled ? `<span class="item-type">Allerede tilføjet</span>` : ''}
            </label>
            ${descriptionBlock}
          </div>
        </div>
      `;
    };

    // Function to render available items
    const renderAvailableItems = (items: DualPanelItem[]) => {
      const showTypes = hasTypes(availableItems);
      const showDescriptions = hasDescriptions(availableItems);
      availableList.innerHTML = items.map((item, index) =>
        renderItemRow(item, index, `avail_${item.value}_${index}`, 'available', showTypes, showDescriptions, !!item.disabled)
      ).join("");

      availableList.querySelectorAll("input[type='checkbox']:not(:disabled)").forEach(checkbox => {
        const input = checkbox as HTMLInputElement;
        input.checked = isAvailableItemPending(input.value);
        input.addEventListener('change', () => {
          setAvailableItemPending(input.value, input.checked);
          updateSelectAllState();
        });
      });
      attachDescriptionReadMoreHandlers(availableList, items);
      updateSelectAllState();
    };

    // Function to render selected items
    const renderSelectedItems = () => {
      const showTypes = hasTypes(runtime.selectedItems);
      const showDescriptions = hasDescriptions(runtime.selectedItems);
      selectedList.innerHTML = runtime.selectedItems.map((item, index) =>
        renderItemRow(item, index, `sel_${item.value}_${index}`, 'selected', showTypes, showDescriptions)
      ).join("");

      attachDescriptionReadMoreHandlers(selectedList, runtime.selectedItems);
      
      // Get the values as an array of IDs (important for correct form submission)
      const selectedValues = runtime.selectedItems.map(item => item.value);
      
      
      // Build the new value without triggering SurveyJS re-render when unchanged
      let newValue: unknown;
      if (questionName === 'event_subtypes') {
        newValue = { event_subtypes: selectedValues };
      } else if (questionName === 'event_modules') {
        newValue = { event_modules: selectedValues };
      } else if (questionName === 'event_users') {
        newValue = { event_users: selectedValues };
      } else {
        newValue = selectedValues;
      }

      const currentSerialized = JSON.stringify(question.value ?? null);
      const newSerialized = JSON.stringify(newValue);
      if (currentSerialized !== newSerialized) {
        question.value = newValue;
      }
    };

    // Initial render: ensure any items that are disabled are not present in selectedItems
    // and that selectedIds from data source are honored
    try {
      const selectedIdsFromData = (items || []).filter((it: any) => it && it.preSelected === true).map((it: any) => String(it.value));
      const assignedSet: Set<string> | undefined = (question as any)['assignedUserIds'];
      if (assignedSet || (selectedIdsFromData && selectedIdsFromData.length > 0)) {
        // Remove any pre-disabled from selected items
        runtime.selectedItems = runtime.selectedItems.filter((si: any) => {
          const val = String(si.value);
          return !(assignedSet && assignedSet.has(val));
        });
        selectedItems = runtime.selectedItems;
      }
    } catch (e) {
      // ignore
    }

    renderAvailableItems(availableItems);
    renderSelectedItems();

    // Handle search
    searchBox.addEventListener("input", () => {
      runtime.currentSearchTerm = searchBox.value.trim().toLowerCase();
      const filteredItems = runtime.availableItems.filter(item => itemMatchesSearch(item, runtime.currentSearchTerm));
      updateSelectAllVisibility();
      renderAvailableItems(filteredItems);
    });

    selectAllCheckbox.addEventListener("change", () => {
      if (!selectAllOnSearchEnabled || !runtime.currentSearchTerm) {
        selectAllCheckbox.checked = false;
        return;
      }

      const visibleCheckboxes = Array.from(
        availableList.querySelectorAll("input[type='checkbox']:not(:disabled)") as NodeListOf<HTMLInputElement>
      );
      for (const checkbox of visibleCheckboxes) {
        checkbox.checked = selectAllCheckbox.checked;
        setAvailableItemPending(checkbox.value, selectAllCheckbox.checked);
      }
      updateSelectAllState();
    });

    // Helper function to apply current search filter
    const applyCurrentSearchFilter = () => {
      if (runtime.currentSearchTerm) {
        const filteredItems = runtime.availableItems.filter(item => itemMatchesSearch(item, runtime.currentSearchTerm));
        updateSelectAllVisibility();
        renderAvailableItems(filteredItems);
      } else {
        updateSelectAllVisibility();
        renderAvailableItems(runtime.availableItems);
      }
    };

    const handleMoveRight = () => {
      const pendingValues = Array.from(runtime.pendingAvailableSelection);
      if (pendingValues.length === 0) {
        return;
      }

      for (const itemValue of pendingValues) {
        const item = runtime.availableItems.find(i => String(i.value) === String(itemValue));
        if (item) {
          const assignedSet: Set<string> | undefined = (question as any)['assignedUserIds'];
          if ((item as any).disabled || (assignedSet && assignedSet.has(String(item.value)))) {
            runtime.pendingAvailableSelection.delete(String(itemValue));
            continue;
          }
          runtime.availableItems = runtime.availableItems.filter(i => String(i.value) !== String(itemValue));
          runtime.selectedItems.push(item);
          runtime.pendingAvailableSelection.delete(String(itemValue));
        } else {
          runtime.pendingAvailableSelection.delete(String(itemValue));
        }
      }

      runtime.selectedItems.sort((a, b) => a.name.localeCompare(b.name));
      availableItems = runtime.availableItems;
      selectedItems = runtime.selectedItems;
      applyCurrentSearchFilter();
      renderSelectedItems();
    };

    const handleMoveLeft = () => {
      const selectedCheckboxes = selectedList.querySelectorAll("input[type='checkbox']:checked") as NodeListOf<HTMLInputElement>;
      if (selectedCheckboxes.length === 0) {
        return;
      }

      for (const checkbox of Array.from(selectedCheckboxes)) {
        const itemValue = checkbox.value;
        const item = runtime.selectedItems.find(i => String(i.value) === String(itemValue));
        if (item) {
          runtime.selectedItems = runtime.selectedItems.filter(i => String(i.value) !== String(itemValue));
          runtime.availableItems.push(item);
        }
      }

      runtime.availableItems.sort((a, b) => a.name.localeCompare(b.name));
      availableItems = runtime.availableItems;
      selectedItems = runtime.selectedItems;
      applyCurrentSearchFilter();
      renderSelectedItems();
    };

    if (!runtime.clickBound) {
      container.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }
        if (target.closest('.move-right')) {
          event.preventDefault();
          event.stopPropagation();
          handleMoveRight();
          return;
        }
        if (target.closest('.move-left')) {
          event.preventDefault();
          event.stopPropagation();
          handleMoveLeft();
        }
      });
      runtime.clickBound = true;
    }

    runtime.handlersReady = true;
    } finally {
      runtime.rendering = false;
    }
  }
};

// Register the widget
CustomWidgetCollection.Instance.add(dualPanelSelector);